import { useEffect, useState } from 'react';
import type { ICustomAlertPreference } from '../../types/api-caller';
import AdminCustomAlertEditor from './AdminCustomAlertEditor';
import AdminCustomAlertSummaryCard from './AdminCustomAlertSummaryCard';
import ToggleSwitch from '../ToggleSwitch';
import { createDefaultCustomAlertPayload } from '../EmailSettings/CustomAlertsSettings';
import { compareCustomAlertsByName } from '../../utils/custom-alerts';

const ADMIN_ALERT_CARD_SELECTOR = '[data-admin-alert-card="true"]';

function getNextGeneratedAlertName(alerts: ICustomAlertPreference[]): string {
  const alertNamePattern = /^Alert\s+(\d+)$/i;
  let highestAlertNumber = 0;

  alerts.forEach((alert) => {
    const match = alert.name?.trim().match(alertNamePattern);
    if (!match) return;

    const parsedAlertNumber = Number(match[1]);
    if (Number.isInteger(parsedAlertNumber) && parsedAlertNumber > highestAlertNumber) {
      highestAlertNumber = parsedAlertNumber;
    }
  });

  return `Alert ${highestAlertNumber + 1}`;
}

interface AdminCustomAlertsSectionProps {
  alerts: ICustomAlertPreference[];
  disabled: boolean;
  syncing?: boolean;
  onCreateAlert: (alert: ICustomAlertPreference) => void;
  onUpdateAlert: (sourceIndex: number, previousAlertName: string, alert: ICustomAlertPreference) => void;
  onDeleteAlert: (alertName: string) => void;
  onToggleAlert: (alertName: string, enabled: boolean) => void;
  onToggleAll?: (enabled: boolean) => void;
}

const AdminCustomAlertsSection: React.FC<AdminCustomAlertsSectionProps> = ({
  alerts,
  disabled,
  syncing = false,
  onCreateAlert,
  onUpdateAlert,
  onDeleteAlert,
  onToggleAlert,
  onToggleAll,
}) => {
  const customAlertsEnabled = alerts.some((a) => a.enabled);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [editingAlertIndex, setEditingAlertIndex] = useState<number | null>(null);
  const [pendingAutoEditAlertName, setPendingAutoEditAlertName] = useState<string | null>(null);

  const isInteractionDisabled = disabled || syncing;

  const createDefaultName = getNextGeneratedAlertName(alerts);
  const sortedAlertEntries = alerts
    .map((alert, sourceIndex) => ({ alert, sourceIndex }))
    .sort((left, right) => {
      const nameComparison = compareCustomAlertsByName(left.alert, right.alert);
      if (nameComparison !== 0) return nameComparison;

      return left.sourceIndex - right.sourceIndex;
    });

  // Click outside to collapse editing card
  useEffect(() => {
    if (editingAlertIndex === null) return;

    function handleClickOutside(e: MouseEvent) {
      const target = e.target;
      if (target instanceof Element && target.closest(ADMIN_ALERT_CARD_SELECTOR)) {
        return;
      }
      setEditingAlertIndex(null);
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingAlertIndex]);

  // Reset create/edit state for non-sync disabled states (e.g., user deselected).
  // Syncing temporarily disables interaction but should not collapse edit mode.
  useEffect(() => {
    if (disabled && !syncing) {
      setIsCreating(false);
      setEditingAlertIndex(null);
      setPendingAutoEditAlertName(null);
    }
  }, [disabled, syncing]);

  useEffect(() => {
    if (!pendingAutoEditAlertName) return;

    const createdAlertIndex = alerts.findIndex((alert) => alert.name === pendingAutoEditAlertName);
    if (createdAlertIndex === -1) return;

    setEditingAlertIndex(createdAlertIndex);
    setIsCreating(false);
    setPendingAutoEditAlertName(null);
  }, [alerts, pendingAutoEditAlertName]);

  function handleEdit(alertIndex: number) {
    setEditingAlertIndex((prev) => (prev === alertIndex ? null : alertIndex));
    setIsCreating(false);
  }

  function handleToggleAll(checked: boolean) {
    if (checked && alerts.length === 0) {
      const defaultAlertPayload = createDefaultCustomAlertPayload(createDefaultName);
      setIsCreating(false);
      setEditingAlertIndex(null);
      setPendingAutoEditAlertName(defaultAlertPayload.name);
      onCreateAlert(defaultAlertPayload);
      return;
    }

    setPendingAutoEditAlertName(null);
    onToggleAll?.(checked);
  }

  return (
    <div
      className={`flex flex-col gap-2 pb-4 transition-opacity ${isInteractionDisabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : ''}`}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="text-lg font-semibold text-black">Custom Alerts</div>
        <ToggleSwitch checked={customAlertsEnabled} onChange={handleToggleAll} disabled={isInteractionDisabled} />
      </div>
      {syncing && <p className="text-gray-500 text-xs">Syncing custom alerts...</p>}
      <hr className="mb-0 mt-0" />

      {!isCreating && (
        <div className="flex py-2">
          <button
            onClick={() => {
              setIsCreating(true);
              setEditingAlertIndex(null);
            }}
            className="bg-ow-blue text-white font-medium px-4 py-2 rounded w-fit hover:opacity-90 transition-opacity text-sm"
          >
            + Create New Custom Alert
          </button>
        </div>
      )}

      {isCreating && (
        <div className="border border-gray-200 rounded p-3">
          <AdminCustomAlertEditor
            mode="create"
            createDefaultName={createDefaultName}
            onSave={(alert) => {
              onCreateAlert(alert);
              setIsCreating(false);
            }}
            onCancel={() => setIsCreating(false)}
          />
        </div>
      )}

      {alerts.length === 0 && !isCreating && <p className="text-gray-400 text-sm">No custom alerts. Click above to create one.</p>}

      {sortedAlertEntries.map(({ alert, sourceIndex }) => (
        <div key={`alert-${sourceIndex}`} data-admin-alert-card="true">
          <hr />
          <AdminCustomAlertSummaryCard
            alert={alert}
            isEditing={editingAlertIndex === sourceIndex}
            onEdit={() => handleEdit(sourceIndex)}
            onDelete={onDeleteAlert}
            onToggle={onToggleAlert}
            onUpdate={(previousAlertName, updatedAlert) => onUpdateAlert(sourceIndex, previousAlertName, updatedAlert)}
          />
        </div>
      ))}
    </div>
  );
};

export default AdminCustomAlertsSection;
